/**
 * The opening message the "Guide me" path sends to Fias AI.
 *
 * "Guide me" used to open a second, throwaway chat inside the New Arche dialog
 * (a `/setup-help/stream` coach with its own history and its own system prompt).
 * That was a worse assistant in a smaller box: no memory, no tools, no arche
 * context, and a transcript the user could never find again. The path now hands
 * the job to the ONE assistant the user already knows — the blue Fias AI button
 * in the header — so setup help lives in their normal conversation history.
 *
 * The brief is written in the user's voice because it IS the user's first
 * message. It carries the exact commands rather than leaving the assistant to
 * recall them: these are FIAS-specific (`fias-dev login --manual`, the
 * scaffolder package name) and a plausible-looking guess costs the user a
 * failed terminal command at the exact moment they are least able to diagnose
 * one.
 */

export type SetupPlatform = 'mac' | 'windows' | 'linux' | 'codespaces';

/** The quickstart template behind the Codespaces path. */
export const QUICKSTART_REPO_URL = 'https://github.com/Enefias/fias-plugin-quickstart';
export const QUICKSTART_GENERATE_URL = 'https://github.com/Enefias/fias-plugin-quickstart/generate';

export const PLATFORM_LABEL: Record<SetupPlatform, string> = {
  mac: 'Mac',
  windows: 'Windows',
  linux: 'Linux',
  codespaces: 'GitHub Codespaces',
};

/** Platform-specific preamble: how this person gets to a usable terminal. */
const ENVIRONMENT_STEPS: Record<SetupPlatform, string> = {
  mac: [
    '1. Get me a terminal with Node.js 18 or newer. I have the macOS Terminal app; Homebrew (`brew install node`) or the installer from nodejs.org both work.',
  ].join('\n'),
  windows: [
    '1. Set up WSL2 so I have a Linux terminal: `wsl --install` from an elevated PowerShell, then a reboot. Everything after this runs in the Ubuntu terminal, not PowerShell — please keep reminding me which one I should be typing in.',
    '2. Install Node.js 18 or newer inside Ubuntu.',
  ].join('\n'),
  linux: [
    '1. Make sure I have Node.js 18 or newer and npm on my PATH. Ask which distro/package manager I have if it matters.',
  ].join('\n'),
  codespaces: [
    `1. Walk me through creating a repo from the FIAS plugin quickstart template (${QUICKSTART_GENERATE_URL} — "Use this template" → "Create a new repository"), then Code → Codespaces → Create codespace on main. The devcontainer scaffolds the plugin and installs dependencies for me, so I do not install anything on my own machine.`,
  ].join('\n'),
};

/** Steps that are the same everywhere once a terminal exists. */
function projectSteps(platform: SetupPlatform, startNum: number): string {
  const lines: string[] = [];
  let n = startNum;
  lines.push(
    `${n++}. Install Claude Code (\`npm install -g @anthropic-ai/claude-code\`, then run \`claude\` and sign in) so I have an AI coding agent in that terminal. Docs: https://docs.claude.com/en/docs/claude-code/overview`,
  );
  if (platform !== 'codespaces') {
    lines.push(
      `${n++}. Scaffold my plugin: \`npm create @fias/fias-plugin@latest my-arche\`, then \`cd my-arche && npm install\`.`,
    );
  }
  lines.push(
    `${n++}. Authenticate the FIAS CLI: \`npx fias-dev login --manual\`. It prints a URL and a short code — I open the URL, paste the code, approve.`,
  );
  lines.push(
    `${n++}. Run \`npm start\` and get my plugin open and rendering in the browser at the URL it prints.`,
  );
  lines.push(
    `${n++}. Show me where to edit (\`src/\`) and how to use Claude Code to build the arche I actually want.`,
  );
  return lines.join('\n');
}

/**
 * Build the opening message for a platform. Sent verbatim as the user's first
 * turn, so it must read like something a person would write.
 */
export function buildPluginSetupBrief(platform: SetupPlatform): string {
  const environment = ENVIRONMENT_STEPS[platform];
  const startNum = environment.split('\n').length + 1;
  return [
    platform === 'codespaces'
      ? 'I want to build a Fias plugin (arche) in GitHub Codespaces, without installing anything on my own computer. Please walk me through the whole setup, step by step.'
      : `I want to build a Fias plugin (arche) on my own machine and I'm on ${PLATFORM_LABEL[platform]}. Please walk me through the whole setup, step by step.`,
    '',
    'Here is the end state I need, in order:',
    '',
    environment,
    projectSteps(platform, startNum),
    '',
    "Please give me ONE step at a time and wait for me to tell you it worked before moving on. Assume I'm not comfortable in a terminal: tell me exactly which window to type in, and what I should expect to see. If I paste an error or some terminal output, explain in plain English what it means before you tell me how to fix it.",
  ].join('\n');
}
